import { Check, ChevronDown, ChevronUp, Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./goal-dropdown.i18n";

type GoalDropdownProps = {
  goals: Array<{ id: string; name: string }>;
  /** The selected goal; null while a global page is showing. */
  currentGoalId: string | null;
  onSelectGoal: (goalId: string) => void;
  onDeleteGoal: (goalId: string) => void;
  canDelete: boolean;
  /** Goals with missing items; only shown once validation errors are on. */
  missingGoalIds: Set<string>;
  showValidationErrors?: boolean;
};

/**
 * QL-593 — the goal tabs collapsed into one menu, used once a flow instance holds more goals
 * than fit as tabs. Sits inside the L1 `TabsList` and looks like a tab: highlighted while a goal
 * is selected, labelled with that goal (or, on a global page, the goal last visited). After a
 * failed Submit it carries the number of incomplete goals, and each one is flagged in the list.
 */
export function GoalDropdown({
  goals,
  currentGoalId,
  onSelectGoal,
  onDeleteGoal,
  canDelete,
  missingGoalIds,
  showValidationErrors,
}: GoalDropdownProps) {
  const t = useT(T);
  const [open, setOpen] = useState(false);

  // Remember the last goal shown, so a global page keeps the trigger labelled with it.
  const [lastGoalId, setLastGoalId] = useState(currentGoalId);
  if (currentGoalId && currentGoalId !== lastGoalId) setLastGoalId(currentGoalId);

  const isActive = currentGoalId !== null && goals.some((g) => g.id === currentGoalId);
  const shownGoal =
    goals.find((g) => g.id === currentGoalId) ?? goals.find((g) => g.id === lastGoalId) ?? goals[0];
  const missingCount = showValidationErrors
    ? goals.filter((g) => missingGoalIds.has(g.id)).length
    : 0;
  const attentionText = t.goalsNeedAttention(String(missingCount));
  const Chevron = open ? ChevronUp : ChevronDown;
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Open on the selected goal rather than the first row: focus it (so arrow keys start there)
  // and scroll it into view. One frame later, so it runs after the menu's own initial focus
  // and once the list has its max-height (scrolling straight away does nothing).
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      const item = selectedItemRef.current;
      if (!item) return;
      item.focus({ preventScroll: true });
      item.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        data-state={isActive ? "active" : "inactive"}
        className={cn(
          // Same look as TabsTrigger, so the menu reads as one of the tabs.
          "inline-flex h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isActive && "bg-card text-foreground shadow-sm",
        )}
      >
        <span className="truncate max-w-[12rem]">{shownGoal?.name}</span>
        {missingCount > 0 && (
          <span
            className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground"
            title={attentionText}
          >
            <span aria-hidden="true">{missingCount}</span>
            <span className="sr-only">{attentionText}</span>
          </span>
        )}
        <Chevron className="h-4 w-4 shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-72 max-h-[min(30rem,var(--radix-dropdown-menu-content-available-height))]"
      >
        {goals.map((goal) => {
          const selected = goal.id === currentGoalId;
          return (
            <DropdownMenuItem
              key={goal.id}
              ref={selected ? selectedItemRef : undefined}
              onSelect={() => onSelectGoal(goal.id)}
              className={cn("gap-2 py-2 pl-8", selected && "bg-accent")}
            >
              {selected && <Check className="absolute left-2 h-4 w-4" />}
              <span className="flex-1 truncate">{goal.name}</span>
              {showValidationErrors && missingGoalIds.has(goal.id) && (
                <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" />
              )}
              {canDelete && (
                <button
                  type="button"
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={`${t.delete} ${goal.name}`}
                  onClick={(e) => {
                    // Delete only — the row's own select must not fire.
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(false);
                    onDeleteGoal(goal.id);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
